import {
  FORMULES_CATALOGUE,
  PUBLIC_FORMULE_FEATURES,
  type FormuleCatalogue,
  type FormuleId,
  type Periodicite,
} from "@/config/formules";

export type { FormuleId, Periodicite } from "@/config/formules";

export type AbonnementStatut = "actif" | "en_attente_paiement" | "brouillon" | "expire" | "resilie";

export interface LimitesFormule {
  immeubles: number | null;
  biens: number | null;
  locataires: number | null;
  contrats: number | null;
  utilisateurs: number | null;
}

export interface Formule {
  id: FormuleId;
  nom: string;
  tagline: string;
  prixMensuel: number;
  prixAnnuel: number;
  highlight: boolean;
  limites: LimitesFormule;
  avantages: string[];
}

/**
 * Formules de l'espace Propriétaire (montants en FCFA).
 * Construites à partir des valeurs lues dans Supabase (source de vérité unique)
 * via `useFormules` : les prix ne peuvent plus diverger de la page publique ou
 * des autres espaces. `null` signifie « illimité ».
 */
export function toEspaceFormules(catalogue: FormuleCatalogue[]): Formule[] {
  return catalogue.map((formule) => ({
    id: formule.id,
    nom: formule.nom,
    tagline: formule.tagline,
    prixMensuel: formule.prixMensuel,
    prixAnnuel: formule.prixAnnuel,
    highlight: formule.highlight,
    limites: {
      immeubles: formule.limites.immeubles,
      biens: formule.limites.biens,
      locataires: formule.limites.locataires,
      contrats: formule.limites.contrats,
      utilisateurs: formule.limites.employes,
    },
    avantages: PUBLIC_FORMULE_FEATURES[formule.id],
  }));
}

export const ABONNEMENT_STATUT_LABELS: Record<AbonnementStatut, string> = {
  actif: "Actif",
  en_attente_paiement: "Paiement requis",
  brouillon: "Non activé",
  expire: "Expiré",
  resilie: "Résilié",
};

export const ABONNEMENT_STATUT_TONES: Record<AbonnementStatut, "success" | "neutral" | "accent" | "danger"> = {
  actif: "success",
  en_attente_paiement: "accent",
  brouillon: "neutral",
  expire: "accent",
  resilie: "danger",
};

export function getFormule(id: FormuleId, catalogue: Formule[] = []): Formule {
  // Le catalogue provient de Supabase (source de vérité). On garantit une liste
  // non vide pour ne jamais retourner `undefined` : si le catalogue n'est pas
  // encore chargé, on retombe sur la configuration locale validée.
  const liste = catalogue.length > 0 ? catalogue : toEspaceFormules(FORMULES_CATALOGUE);
  return liste.find((formule) => formule.id === id) ?? liste[0];
}

/**
 * Modèle de données prévu pour Supabase.
 * Table cible : `abonnements`
 *   id uuid primary key default gen_random_uuid()
 *   owner_id uuid references auth.users(id)
 *   formule_id text not null, periodicite text not null, statut text not null
 *   date_debut date, date_fin date
 *   created_at timestamptz default now()
 * Les prix et les limites proviennent de `@/config/formules` (table `formules`).
 */